import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { useArticleById, useUpdateArticleAdmin } from '@/hooks/useArticlesAdmin';

// Schéma de validation
const articleSchema = z.object({
  title: z.string().min(1, 'Le titre est obligatoire'),
  slug: z.string().min(1, 'Le slug est obligatoire'),
  excerpt: z.string().min(1, "L'extrait est obligatoire"),
  category: z.string().nullable().optional(),
  tags: z.string().optional(),
  featured_image: z.string().nullable().optional(),
  status: z.enum(['draft', 'published', 'archived']),
  is_premium: z.boolean(),
  price: z.coerce.number().nullable().optional(),
  published_at: z.string().nullable().optional(),
  content: z.string().min(1, 'Le contenu est obligatoire'),
  meta_title: z.string().nullable().optional(),
  meta_description: z.string().nullable().optional(),
});

type ArticleFormData = z.infer<typeof articleSchema>;

export function ArticleEdit() {
  const { id, locale } = useParams<{ id: string; locale: string }>();
  const navigate = useNavigate();
  const currentLocale = locale || 'fr';

  const { data: article, isLoading, error } = useArticleById(id);
  const updateArticle = useUpdateArticleAdmin();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<ArticleFormData>({
    resolver: zodResolver(articleSchema),
    defaultValues: {
      status: 'draft',
      is_premium: false,
    },
  });

  // Remplir le formulaire quand l'article est chargé
  useEffect(() => {
    if (article) {
      reset({
        title: article.title,
        slug: article.slug,
        excerpt: article.excerpt,
        category: article.category,
        tags: article.tags?.join(', ') || '',
        featured_image: article.featured_image,
        status: article.status,
        is_premium: article.is_premium,
        price: article.price,
        published_at: article.published_at
          ? article.published_at.slice(0, 16) // Format datetime-local
          : null,
        content: article.content,
        meta_title: article.meta_title,
        meta_description: article.meta_description,
      });
    }
  }, [article, reset]);

  const currentStatus = watch('status');
  const isPremium = watch('is_premium');

  const onSubmit = async (data: ArticleFormData) => {
    if (!id) return;

    // Transformer les tags en tableau
    const tags = data.tags
      ? data.tags.split(',').map((t) => t.trim()).filter(Boolean)
      : null;

    // Si on publie et pas de date, mettre la date actuelle
    let publishedAt = data.published_at || null;
    if (data.status === 'published' && !publishedAt) {
      publishedAt = new Date().toISOString();
    }

    try {
      await updateArticle.mutateAsync({
        id,
        updates: {
          title: data.title,
          slug: data.slug,
          excerpt: data.excerpt,
          category: data.category || null,
          tags,
          featured_image: data.featured_image || null,
          status: data.status,
          is_premium: data.is_premium,
          price: data.is_premium ? (data.price || null) : null,
          published_at: publishedAt,
          content: data.content,
          meta_title: data.meta_title || null,
          meta_description: data.meta_description || null,
        },
      });
      // Recharger les données après sauvegarde
      navigate(`/${currentLocale}/admin/articles`);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde:', err);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-16">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-500">Chargement de l'article...</span>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-600 mb-4">
          {error ? `Erreur : ${error.message}` : 'Article introuvable'}
        </p>
        <Button
          variant="outline"
          onClick={() => navigate(`/${currentLocale}/admin/articles`)}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux articles
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(`/${currentLocale}/admin/articles`)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <h1 className="text-2xl font-bold">Éditer l'article</h1>
        </div>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={updateArticle.isPending || !isDirty}
        >
          {updateArticle.isPending ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          {updateArticle.isPending ? 'Sauvegarde...' : 'Sauvegarder'}
        </Button>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Section : Informations principales */}
        <Card>
          <CardHeader>
            <CardTitle>Informations principales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Titre */}
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                {...register('title')}
                aria-invalid={!!errors.title}
              />
              {errors.title && (
                <p className="text-sm text-red-600">{errors.title.message}</p>
              )}
            </div>

            {/* Slug */}
            <div className="space-y-2">
              <Label htmlFor="slug">Slug *</Label>
              <Input
                id="slug"
                {...register('slug')}
                aria-invalid={!!errors.slug}
              />
              {errors.slug && (
                <p className="text-sm text-red-600">{errors.slug.message}</p>
              )}
            </div>

            {/* Extrait */}
            <div className="space-y-2">
              <Label htmlFor="excerpt">Extrait *</Label>
              <Textarea
                id="excerpt"
                rows={3}
                {...register('excerpt')}
                aria-invalid={!!errors.excerpt}
              />
              {errors.excerpt && (
                <p className="text-sm text-red-600">{errors.excerpt.message}</p>
              )}
            </div>

            {/* Catégorie + Tags */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Catégorie</Label>
                <Input id="category" {...register('category')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="tags">Tags (séparés par des virgules)</Label>
                <Input id="tags" {...register('tags')} placeholder="tag1, tag2, tag3" />
              </div>
            </div>

            {/* Image mise en avant */}
            <div className="space-y-2">
              <Label htmlFor="featured_image">Image mise en avant (URL)</Label>
              <Input id="featured_image" {...register('featured_image')} />
            </div>
          </CardContent>
        </Card>

        {/* Section : Statut & Publication */}
        <Card>
          <CardHeader>
            <CardTitle>Statut & Publication</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Statut */}
              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={currentStatus}
                  onValueChange={(value) =>
                    setValue('status', value as ArticleFormData['status'], { shouldDirty: true })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="published">Publié</SelectItem>
                    <SelectItem value="draft">Brouillon</SelectItem>
                    <SelectItem value="archived">Archivé</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Date de publication */}
              <div className="space-y-2">
                <Label htmlFor="published_at">Date de publication</Label>
                <Input
                  id="published_at"
                  type="datetime-local"
                  {...register('published_at')}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Premium */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    {...register('is_premium')}
                    className="rounded border-gray-300"
                  />
                  Article premium (payant)
                </Label>
              </div>

              {/* Prix */}
              {isPremium && (
                <div className="space-y-2">
                  <Label htmlFor="price">Prix (EUR)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    min="0"
                    {...register('price')}
                  />
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section : Contenu */}
        <Card>
          <CardHeader>
            <CardTitle>Contenu</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="content">Contenu (Markdown) *</Label>
              <Textarea
                id="content"
                rows={20}
                className="font-mono text-sm"
                {...register('content')}
                aria-invalid={!!errors.content}
              />
              {errors.content && (
                <p className="text-sm text-red-600">{errors.content.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section : SEO */}
        <Card>
          <CardHeader>
            <CardTitle>SEO</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="meta_title">Meta title</Label>
              <Input id="meta_title" {...register('meta_title')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="meta_description">Meta description</Label>
              <Textarea
                id="meta_description"
                rows={2}
                {...register('meta_description')}
              />
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
